import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileArchive,
  FileText,
  History,
  Image as ImageIcon,
  Info,
  KeyRound,
  Link2,
  Loader2,
  Package,
  PackageCheck,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AdminModal, BulkDeleteBar, Pill, SelectCheckbox, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";
import { formatPrice, formatDateTime } from "@/lib/format";
import ProductDeliveryForm, { TYPE_ICON, TYPE_LABEL } from "@/components/admin/ProductDeliveryForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 10;

const STATUS_META = {
  active: { label: "Actif", tone: "green" },
  pending: { label: "En attente", tone: "amber" },
  disabled: { label: "Désactivé", tone: "rose" },
};

const CHIP_META = [
  { key: "file", singular: "fichier", plural: "fichiers", icon: FileArchive, cls: "border-brand/20 bg-brand-light text-brand" },
  { key: "link", singular: "lien", plural: "liens", icon: Link2, cls: "border-sky-200 bg-sky-50 text-sky-700" },
  { key: "key", singular: "clé", plural: "clés", icon: KeyRound, cls: "border-amber-200 bg-amber-50 text-amber-700" },
  { key: "image", singular: "image", plural: "images", icon: ImageIcon, cls: "border-rose-200 bg-rose-50 text-rose-700" },
  { key: "text", singular: "texte", plural: "textes", icon: FileText, cls: "border-slate-200 bg-slate-100 text-slate-600" },
];

function ContentChips({ counts }) {
  const chips = CHIP_META.filter((c) => (counts?.[c.key] || 0) > 0);
  if (!chips.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <span key={c.key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${c.cls}`}>
          <c.icon className="h-3 w-3" /> {counts[c.key]} {counts[c.key] > 1 ? c.plural : c.singular}
        </span>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
}

function DetailPanel({ detail, loading, onClose, onManage }) {
  if (loading) {
    return (
      <aside className="rounded-2xl border border-line bg-white p-6" data-testid="delivery-detail-panel">
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      </aside>
    );
  }
  if (!detail) return null;
  const p = detail.product || {};
  return (
    <aside className="h-fit rounded-2xl border border-line bg-white p-5" data-testid="delivery-detail-panel">
      <div className="flex items-start justify-between gap-2">
        <p className="font-display text-sm font-extrabold text-ink">Détail de la livraison</p>
        <button type="button" onClick={onClose} data-testid="delivery-panel-close"
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-ink">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-3">
        {p.image_url ? (
          <img src={p.image_url} alt="" className="h-20 w-16 shrink-0 rounded-xl border border-line object-cover" />
        ) : (
          <span className="flex h-20 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100">
            <Package className="h-6 w-6 text-slate-300" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-ink">{p.name}</p>
          <p className="text-xs text-slate-500">{p.category || "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Prix : <span className="font-bold text-ink">{formatPrice(p.price || 0, "XOF")}</span></p>
          <p className="text-[11px] text-slate-400">Référence : {detail.product_id}</p>
          {p.platform && <p className="text-[11px] text-slate-400">Plateforme : {p.platform}</p>}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-line p-3.5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Informations</p>
        <div className="mt-2 space-y-1.5 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">Statut</span><StatusPill status={detail.status} /></div>
          <div className="flex justify-between"><span className="text-slate-500">Créée le</span><span className="font-semibold text-ink">{formatDateTime(detail.created_at)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Mise à jour</span><span className="font-semibold text-ink">{formatDateTime(detail.updated_at)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Total contenus</span><span className="font-semibold text-ink">{(detail.items || []).filter((i) => !i.is_hidden).length} éléments</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Téléchargements</span><span className="font-semibold text-ink">{detail.downloads}</span></div>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Contenus associés</p>
        <div className="mt-2 space-y-2" data-testid="delivery-panel-contents">
          {(detail.items || []).length === 0 ? (
            <p className="rounded-xl border border-dashed border-line py-6 text-center text-xs text-slate-400">Aucun contenu configuré</p>
          ) : (
            detail.items.map((item, i) => {
              const Icon = TYPE_ICON[item.type] || FileText;
              return (
                <div key={item.id || i} className="flex items-center gap-2.5 rounded-xl border border-line p-2.5" data-testid={`delivery-panel-item-${i}`}>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-bold text-ink">{item.title}</p>
                    <p className="truncate text-[11px] text-slate-400">
                      {item.file_name || item.url || (item.type === "key" ? item.value : TYPE_LABEL[item.type])}
                    </p>
                  </div>
                  {item.is_hidden ? <Pill tone="slate">Désactivé</Pill> : <Pill tone="green">Actif</Pill>}
                </div>
              );
            })
          )}
        </div>
      </div>

      {(detail.orders || []).length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Commandes liées ({detail.orders.length})</p>
          <div className="mt-2 max-h-48 space-y-1.5 overflow-y-auto" data-testid="delivery-panel-orders">
            {detail.orders.map((o) => (
              <Link key={o.id} to={`/admin/livraisons/${o.id}`} data-testid={`delivery-panel-order-${o.order_number}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2 text-xs transition-colors duration-200 hover:border-brand">
                <span className="min-w-0">
                  <span className="block truncate font-bold text-ink">#{o.order_number}</span>
                  <span className="block truncate text-[11px] text-slate-400">{o.customer?.name} · {formatDateTime(o.created_at)}</span>
                </span>
                {o.delivery?.status === "released"
                  ? <Pill tone="green">Validée</Pill>
                  : o.delivery?.items_count > 0 ? <Pill tone="amber">Prête</Pill> : <Pill tone="slate">À préparer</Pill>}
              </Link>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">
            Ouvrez une commande pour personnaliser son contenu (clé ou lien unique par client) et valider le paiement.
          </p>
        </div>
      )}

      <button
        type="button"
        data-testid="delivery-manage-contents-btn"
        onClick={onManage}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
      >
        <Pencil className="h-3.5 w-3.5" /> Gérer les contenus
      </button>
    </aside>
  );
}

function JournalModal({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/product-deliveries/journal", { params: { q: query } });
      setRows(data.items || []);
    } catch (error) {
      toast.error(apiError(error, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load(q);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    debounce.current && clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(q), 300);
    return () => clearTimeout(debounce.current);
  }, [q, open, load]);

  return (
    <AdminModal open={open} onOpenChange={(v) => !v && onClose()} wide title="Journal des téléchargements">
      <div className="space-y-4" data-testid="delivery-journal-modal">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="delivery-journal-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Client, produit, commande, contenu…" className={`${inputCls} pl-9`} />
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-xs text-slate-400">Aucun téléchargement enregistré</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-line text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-3">Client</th>
                  <th className="pb-2 pr-3">Produit</th>
                  <th className="pb-2 pr-3">Commande</th>
                  <th className="pb-2 pr-3">Contenu</th>
                  <th className="pb-2 pr-3">Date</th>
                  <th className="pb-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-line/60 text-xs last:border-0" data-testid={`delivery-journal-row-${r.id}`}>
                    <td className="py-2.5 pr-3">
                      <span className="block font-bold text-ink">{r.customer_name || "—"}</span>
                      <span className="block text-[11px] text-slate-400">{r.customer_email}</span>
                    </td>
                    <td className="max-w-[140px] truncate py-2.5 pr-3 font-semibold text-ink">{r.product_name}</td>
                    <td className="py-2.5 pr-3 text-slate-500">#{r.order_number}</td>
                    <td className="max-w-[160px] truncate py-2.5 pr-3 text-slate-600">{r.item_title || r.file_name}</td>
                    <td className="whitespace-nowrap py-2.5 pr-3 text-slate-500">{formatDateTime(r.at)}</td>
                    <td className="py-2.5">
                      {r.status === "success"
                        ? <Pill tone="green">Succès</Pill>
                        : r.status === "blocked_expired" ? <Pill tone="rose">Expiré</Pill> : <Pill tone="amber">Bloqué</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminModal>
  );
}

export default function AdminDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formDelivery, setFormDelivery] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeText, setNoticeText] = useState("");
  const [savingNotice, setSavingNotice] = useState(false);
  const debounce = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/product-deliveries", {
        params: { q, status: statusFilter, category: categoryFilter },
      });
      setDeliveries(data.items || []);
      setStats(data.stats || null);
    } catch (error) {
      toast.error(apiError(error, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, categoryFilter]);

  const bulk = useBulkDelete("/admin/product-deliveries/bulk-delete", load, "livraison(s)");

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCategories(data.items || data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    debounce.current && clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setQ(search); setPage(1); }, 300);
    return () => clearTimeout(debounce.current);
  }, [search]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    api.get(`/admin/product-deliveries/${selectedId}`)
      .then(({ data }) => setDetail(data))
      .catch((e) => toast.error(apiError(e, "Chargement impossible")))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const openNotice = async () => {
    try {
      const { data } = await api.get("/admin/digital-delivery/settings");
      setNoticeText(data.important_text || "");
      setNoticeOpen(true);
    } catch (error) {
      toast.error(apiError(error, "Chargement impossible"));
    }
  };

  const saveNotice = async () => {
    setSavingNotice(true);
    try {
      await api.put("/admin/digital-delivery/settings", { important_text: noticeText });
      toast.success("Texte d'information enregistré");
      setNoticeOpen(false);
    } catch (error) {
      toast.error(apiError(error, "Enregistrement impossible"));
    } finally {
      setSavingNotice(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/product-deliveries/${deleteTarget.id}`);
      toast.success("Livraison supprimée");
      setDeleteTarget(null);
      if (selectedId === deleteTarget.id) { setSelectedId(""); setDetail(null); }
      load();
    } catch (error) {
      toast.error(apiError(error, "Suppression impossible"));
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = () => { setFormDelivery(null); setFormOpen(true); };
  const openEdit = (d) => { setFormDelivery(d); setFormOpen(true); };
  const onSaved = () => { load(); if (selectedId) setSelectedId(""); };

  const pages = Math.max(1, Math.ceil(deliveries.length / PAGE_SIZE));
  const pageItems = deliveries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const STAT_CARDS = [
    { key: "total", label: "Total livraisons", sub: "Toutes les livraisons", icon: Package, cls: "bg-brand-light text-brand" },
    { key: "active", label: "Actives", sub: "Livraisons actives", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-600" },
    { key: "pending", label: "En attente", sub: "En attente de contenu", icon: Clock, cls: "bg-amber-50 text-amber-600" },
    { key: "disabled", label: "Désactivées", sub: "Livraisons désactivées", icon: XCircle, cls: "bg-rose-50 text-rose-600" },
    { key: "downloads", label: "Téléchargements", sub: `${stats?.validated_orders ?? 0} commandes validées`, icon: Download, cls: "bg-sky-50 text-sky-600" },
  ];

  return (
    <div data-testid="admin-deliveries-page">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Livraisons numériques</h1>
          <p className="mt-1 text-sm text-slate-500">
            Gérez les fichiers, liens, licences et contenus qui seront automatiquement remis aux clients après validation de leur commande.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-testid="delivery-journal-btn" onClick={() => setJournalOpen(true)}
                  className="flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-slate-600 transition-colors duration-200 hover:border-brand hover:text-brand">
            <History className="h-3.5 w-3.5" /> Journal
          </button>
          <button type="button" data-testid="delivery-notice-btn" onClick={openNotice}
                  className="flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-slate-600 transition-colors duration-200 hover:border-brand hover:text-brand">
            <Info className="h-3.5 w-3.5" /> Texte « Important »
          </button>
          <button type="button" data-testid="delivery-add-btn" onClick={openCreate}
                  className="flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white shadow-sm transition-colors duration-200 hover:bg-brand/90">
            <Plus className="h-4 w-4" /> Ajouter une livraison numérique
          </button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5" data-testid="deliveries-stats">
        {STAT_CARDS.map((card) => (
          <div key={card.key} className="rounded-2xl border border-line bg-white p-4" data-testid={`delivery-stat-${card.key}`}>
            <div className="flex items-center gap-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${card.cls}`}>
                <card.icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500">{card.label}</p>
                <p className="font-display text-xl font-extrabold text-ink">{stats?.[card.key] ?? "—"}</p>
              </div>
            </div>
            <p className="mt-2 truncate text-[11px] text-slate-400">{card.sub}</p>
          </div>
        ))}
      </div>

      <div className={`grid gap-5 ${detail || detailLoading ? "xl:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="deliveries-search" value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Rechercher une livraison…" className={`${inputCls} pl-9`} />
            </div>
            <select data-testid="deliveries-status-filter" value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className="h-11 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 focus:border-brand focus:outline-none">
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="pending">En attente</option>
              <option value="disabled">Désactivé</option>
            </select>
            <select data-testid="deliveries-category-filter" value={categoryFilter}
                    onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                    className="h-11 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 focus:border-brand focus:outline-none">
              <option value="">Toutes les catégories</option>
              {categories.map((c) => <option key={c.slug || c.id} value={c.slug}>{c.name}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center rounded-2xl border border-line bg-white py-16">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white py-16 text-center" data-testid="deliveries-empty">
              <PackageCheck className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-500">Aucune livraison numérique configurée</p>
              <button type="button" onClick={openCreate}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-brand/90">
                <Plus className="h-4 w-4" /> Ajouter une livraison numérique
              </button>
            </div>
          ) : (
            <div className="rounded-2xl border border-line bg-white" data-testid="deliveries-list">
              <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-deliveries" />
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left">
                  <thead>
                    <tr className="border-b border-line text-[11px] font-bold uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-3 w-9">
                        <SelectCheckbox testId="bulk-all-deliveries"
                          checked={pageItems.length > 0 && bulk.selected.length >= pageItems.length}
                          onChange={() => bulk.toggleAll(pageItems.map((d) => d.id))} />
                      </th>
                      <th className="px-4 py-3">Produit</th>
                      <th className="px-3 py-3">Contenus</th>
                      <th className="px-3 py-3">Statut</th>
                      <th className="px-3 py-3">Téléchargements</th>
                      <th className="px-3 py-3">Dernière mise à jour</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((d) => (
                      <tr key={d.id}
                          className={`cursor-pointer border-b border-line/60 transition-colors duration-150 last:border-0 hover:bg-brand-light/30 ${selectedId === d.id ? "bg-brand-light/40" : ""}`}
                          onClick={() => setSelectedId(d.id)}
                          data-testid={`delivery-row-${d.id}`}>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <SelectCheckbox testId={`bulk-delivery-${d.id}`}
                            checked={bulk.selected.includes(d.id)} onChange={() => bulk.toggle(d.id)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {d.product.image_url ? (
                              <img src={d.product.image_url} alt="" className="h-11 w-11 shrink-0 rounded-lg border border-line object-cover" />
                            ) : (
                              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                                <Package className="h-5 w-5 text-slate-300" />
                              </span>
                            )}
                            <div className="min-w-0">
                              <p className="max-w-[200px] truncate text-sm font-bold text-ink">{d.product.name}</p>
                              <p className="text-xs text-slate-400">{d.product.category || "—"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3"><ContentChips counts={d.counts} /></td>
                        <td className="px-3 py-3"><StatusPill status={d.status} /></td>
                        <td className="px-3 py-3 text-sm font-semibold text-ink">{d.downloads}</td>
                        <td className="px-3 py-3 text-xs text-slate-500">{formatDateTime(d.updated_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <button type="button" data-testid={`delivery-view-${d.id}`} onClick={() => setSelectedId(d.id)}
                                    className="rounded-lg p-2 text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-ink" title="Voir le détail">
                              <Eye className="h-4 w-4" />
                            </button>
                            <button type="button" data-testid={`delivery-edit-${d.id}`} onClick={() => openEdit(d)}
                                    className="rounded-lg p-2 text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-brand" title="Modifier">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button type="button" data-testid={`delivery-delete-${d.id}`} onClick={() => setDeleteTarget(d)}
                                    className="rounded-lg p-2 text-slate-400 transition-colors duration-150 hover:bg-rose-50 hover:text-rose-600" title="Supprimer">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
                <p className="text-xs text-slate-400">
                  Affichage de {(page - 1) * PAGE_SIZE + 1} à {Math.min(page * PAGE_SIZE, deliveries.length)} sur {deliveries.length} résultats
                </p>
                <div className="flex items-center gap-1.5">
                  <button type="button" data-testid="deliveries-page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                          className="rounded-lg border border-line p-1.5 text-slate-500 transition-colors duration-150 hover:border-brand hover:text-brand disabled:opacity-40">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="rounded-lg bg-brand px-3 py-1 text-xs font-bold text-white" data-testid="deliveries-page-current">{page}</span>
                  <span className="text-xs text-slate-400">/ {pages}</span>
                  <button type="button" data-testid="deliveries-page-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                          className="rounded-lg border border-line p-1.5 text-slate-500 transition-colors duration-150 hover:border-brand hover:text-brand disabled:opacity-40">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {(detail || detailLoading) && (
          <DetailPanel
            detail={detail}
            loading={detailLoading}
            onClose={() => { setSelectedId(""); setDetail(null); }}
            onManage={() => detail && openEdit({ id: detail.id })}
          />
        )}
      </div>

      <ProductDeliveryForm
        open={formOpen}
        delivery={formDelivery}
        onClose={() => setFormOpen(false)}
        onSaved={onSaved}
      />

      <JournalModal open={journalOpen} onClose={() => setJournalOpen(false)} />

      <AdminModal open={noticeOpen} onOpenChange={(v) => !v && setNoticeOpen(false)} title="Texte « Important » de la page de téléchargement">
        <div className="space-y-4" data-testid="delivery-notice-modal">
          <p className="text-xs text-slate-500">
            Ce texte est affiché dans la carte « ℹ️ Important » sur la page « Lancer le téléchargement » côté client.
          </p>
          <textarea data-testid="delivery-notice-textarea" value={noticeText} onChange={(e) => setNoticeText(e.target.value)}
                    rows={5} className={textareaCls} />
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button type="button" onClick={() => setNoticeOpen(false)}
                    className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-ink">
              Annuler
            </button>
            <button type="button" data-testid="delivery-notice-save" onClick={saveNotice} disabled={savingNotice}
                    className="rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90 disabled:opacity-60">
              {savingNotice ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </AdminModal>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delivery-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette livraison numérique ?</AlertDialogTitle>
            <AlertDialogDescription>
              La configuration de « {deleteTarget?.product?.name} » et ses contenus seront supprimés. Les livraisons déjà validées sur les commandes passées ne sont pas affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delivery-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="delivery-delete-confirm-btn" onClick={confirmDelete} disabled={deleting}
                               className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
